import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
    Document,
    Page,
    Text as PDFText,
    View,
    Image,
    StyleSheet,
    PDFDownloadLink,
    // PDFViewer,
    Font,
} from '@react-pdf/renderer';

import {
    Text,
    StyledCard,
    StyledCardContent,
    NoInformation,
    CalendarIcon2,
    HospitalIcon,
    OutlinedButton,
    ContainedButton,
    DownloadIcon,
} from '../../index';

import { StyledIconContainer, StyledInfoContainer, StyledTextContainer } from './style';

const PatientPresentRecord = ({ data }) => {
    const history = useHistory();
    const [today] = useState(dayjs());

    const handleReport = () => {
        history.push(`/provider/session/report/${data.session.meet.id}`);
    };

    // Register Font
    Font.register({
        family: 'Prompt',
        src: '/resources/fonts/Prompt-regular.ttf',
    });
    Font.register({
        family: 'PromptBold',
        src: '/resources/fonts/Prompt-SemiBold.ttf',
    });

    // Create styles
    const styles = StyleSheet.create({
        page: {
            fontFamily: 'Prompt',
            flexDirection: 'row',
            padding: '40px 30px',
        },
        avatar: {
            width: '50px',
            height: '50px',
            borderRadius: '16px',
        },
        container: {},
        card: {
            display: 'flex',
            flexDirection: 'row',
            marginBottom: '20px',
        },
        contentContainer: {
            display: 'flex',
            flexDirection: 'column',
            padding: '0 0 5px 20px',
            justifyContent: 'space-between',
        },
        name: {
            fontSize: '18px',
            marginBottom: '8px',
        },
        value: {
            fontSize: '14px',
        },
        header: {
            fontSize: '16px',
            fontFamily: 'PromptBold',
        },
    });

    const MyDocument = useMemo(() => {
        return (
            <Document>
                <Page size="A4" style={styles.page}>
                    <View style={styles.container}>
                        <View style={[styles.card, { paddingLeft: '20px' }]}>
                            {data?.patient?.user?.avatar && (
                                <Image
                                    style={styles.avatar}
                                    src={data?.patient?.user?.avatar?.url}
                                />
                            )}
                            <View>
                                <View style={styles.contentContainer}>
                                    <PDFText
                                        style={styles.name}
                                        render={() => `${data?.patient?.user?.fullname}`}
                                        fixed
                                    />
                                    <PDFText
                                        style={styles.value}
                                        render={() => `ID: ${data.patient.HNCode}`}
                                        fixed
                                    />

                                    {data?.patient?.user?.birthday && (
                                        <PDFText
                                            style={styles.value}
                                            render={() =>
                                                `Age: ${dayjs().diff(
                                                    dayjs(data.patient?.user?.birthday),
                                                    'year'
                                                )}`
                                            }
                                            fixed
                                        />
                                    )}
                                    <PDFText
                                        style={styles.value}
                                        render={() => `Gender: ${data?.patient?.user?.gender}`}
                                        fixed
                                    />
                                    <View
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                        }}
                                    >
                                        <PDFText
                                            style={styles.value}
                                            render={() => `Client Tag: `}
                                            fixed
                                        />
                                        {data?.patient?.patientTag.map((item, index) => (
                                            <PDFText
                                                style={[styles.value, { marginRight: '10px' }]}
                                                render={() =>
                                                    data.patient.patientTag.length !== index + 1
                                                        ? `${item},`
                                                        : item
                                                }
                                                key={item + index}
                                            />
                                        ))}
                                    </View>
                                </View>
                            </View>
                        </View>

                        <View style={styles.card}>
                            <View style={styles.contentContainer}>
                                <PDFText
                                    style={{
                                        fontSize: '18px',
                                        fontFamily: 'PromptBold',
                                        marginBottom: '5px',
                                    }}
                                    render={() => `Activity name - ${data?.session?.meet?.name}`}
                                />
                                <PDFText
                                    style={styles.value}
                                    render={() =>
                                        `Date: ${dayjs(data.joinTime).format('dddd')}, ${dayjs(
                                            data.joinTime
                                        ).format('D MMMM')}, ${dayjs(data.joinTime).format(
                                            'HH:mm'
                                        )} - ${dayjs(data.leaveTime).format('HH:mm')}`
                                    }
                                />
                                <PDFText
                                    style={styles.value}
                                    render={() =>
                                        `Counseling Service: ${data.session.meet.hospital.name}`
                                    }
                                />
                            </View>
                        </View>

                        <View style={styles.card}>
                            <View style={styles.contentContainer}>
                                <PDFText
                                    style={styles.header}
                                    render={() => `Mental Status Examination (MSE)`}
                                />
                                <PDFText style={styles.value} render={() => data.MSE} />
                            </View>
                        </View>

                        {/* <View style={styles.card}>
                            <View style={styles.contentContainer}>
                                <PDFText style={styles.header} render={() => `Present History`} />
                                <PDFText
                                    style={[styles.value, { marginBottom: '10px' }]}
                                    render={() => data.presentHistory}
                                />
                                <PDFText style={styles.header} render={() => `Medical History`} />
                                <PDFText
                                    style={[styles.value, { marginBottom: '10px' }]}
                                    render={() => data.medicalHistory}
                                />
                                <PDFText style={styles.header} render={() => `Family History`} />
                                <PDFText
                                    style={[styles.value, { marginBottom: '10px' }]}
                                    render={() => data.familyHistory}
                                />
                            </View>
                        </View> */}

                        <View style={styles.card}>
                            <View style={styles.contentContainer}>
                                <PDFText style={styles.header} render={() => `Diagnosis`} />
                                <PDFText
                                    style={[styles.value, { marginBottom: '10px' }]}
                                    render={() => data.diagnosis}
                                />
                                <PDFText style={styles.header} render={() => `Note to Provider`} />
                                <PDFText
                                    style={[styles.value, { marginBottom: '10px' }]}
                                    render={() => data.noteToPsychiatrist}
                                />
                            </View>
                        </View>

                        <View style={styles.card}>
                            <View style={styles.contentContainer}>
                                <PDFText style={styles.header} render={() => `Note to Client`} />
                                <PDFText
                                    style={[styles.value, { marginBottom: '10px' }]}
                                    render={() => data.noteToPatient}
                                />
                                <PDFText style={styles.header} render={() => `Homework`} />
                                <PDFText
                                    style={[styles.value, { marginBottom: '10px' }]}
                                    render={() => data.homework}
                                />
                            </View>
                        </View>
                    </View>
                </Page>
            </Document>
        );
    }, [data]);

    return !_.isEmpty(data) ? (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',

                rowGap: '15px',
            }}
        >
            <StyledCard
                style={{
                    padding: '15px 20px',
                }}
            >
                <StyledCardContent
                    style={{ display: 'flex', flexDirection: 'column', rowGap: '7px' }}
                >
                    <Image src={data?.patient?.user?.avatar?.url} />
                    <Text $fontSize="18px" $bold style={{ marginBottom: '5px' }}>
                        Activity name - {data?.session?.meet?.name}
                    </Text>
                    <StyledInfoContainer>
                        <StyledIconContainer>
                            <CalendarIcon2 fill="blue3" />
                        </StyledIconContainer>
                        <Text $fontSize="14px" $color="blue3">
                            {`${
                                dayjs(data.joinTime).isSame(today, 'day')
                                    ? 'Today'
                                    : dayjs(data.joinTime).format('dddd')
                            }, ${dayjs(data.joinTime).format('D MMMM')}, ${dayjs(
                                data?.joinTime
                            ).format('HH:mm')} - ${dayjs(data?.leaveTime).format('HH:mm')}`}
                        </Text>
                    </StyledInfoContainer>
                    <StyledInfoContainer>
                        <StyledIconContainer>
                            <HospitalIcon fill="blue3" />
                        </StyledIconContainer>
                        <Text $fontSize="14px" $color="blue3">
                            {data?.session?.meet?.hospital?.name}
                        </Text>
                    </StyledInfoContainer>
                </StyledCardContent>
            </StyledCard>

            <StyledCard>
                <StyledCardContent
                    style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}
                >
                    <Text $fontSize="18px" $bold>
                        Mental Status Examination (MSE)
                    </Text>
                    <Text>{data?.MSE}</Text>
                </StyledCardContent>
            </StyledCard>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    columnGap: '15px',
                }}
            >
                {/* <StyledTextContainer>
                    <Text $fontSize="18px" $bold>
                        Present History
                    </Text>
                    <Text>{data?.presentHistory}</Text>
                    <Text $fontSize="18px" $bold>
                        Medical History
                    </Text>
                    <Text>{data?.medicalHistory}</Text>
                    <Text $fontSize="18px" $bold>
                        Family History
                    </Text>
                    <Text>{data?.familyHistory}</Text>
                </StyledTextContainer> */}

                <StyledTextContainer>
                    <Text $fontSize="18px" $bold>
                        Diagnosis
                    </Text>
                    <Text>{data?.diagnosis}</Text>
                    <Text $fontSize="18px" $bold>
                        Treatment
                    </Text>
                    <Text>{data?.treatment}</Text>
                    <Text $fontSize="18px" $bold>
                        Note to Provider
                    </Text>
                    <Text>{data?.noteToPsychiatrist}</Text>
                </StyledTextContainer>

                <StyledTextContainer>
                    <Text $fontSize="18px" $bold>
                        Note to Client
                    </Text>
                    <Text>{data?.noteToPatient}</Text>
                    <Text $fontSize="18px" $bold>
                        Homework
                    </Text>
                    <Text>{data?.homework}</Text>
                </StyledTextContainer>
            </div>

            <div
                style={{
                    gridColumnEnd: 'span 2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    columnGap: '10px',
                }}
            >
                <PDFDownloadLink
                    document={MyDocument}
                    fileName={`${data?.patient?.user?.fullname}.pdf`}
                    style={{ textDecoration: 'none' }}
                >
                    {({ loading }) => (
                        <OutlinedButton
                            style={{
                                borderRadius: '8px',
                                width: '165px',
                                height: '38px',
                            }}
                        >
                            <Text $color="blue3" $fontSize="14px" style={{ marginRight: '10px' }}>
                                {loading ? 'Loading' : 'Download'}
                            </Text>
                            {!loading && <DownloadIcon fill="blue3" width="15px" height="15px" />}
                        </OutlinedButton>
                    )}
                </PDFDownloadLink>
                <ContainedButton
                    style={{ borderRadius: '8px', width: '240px', height: '38px' }}
                    onClick={handleReport}
                >
                    <Text $color="white" $fontSize="14px">
                        View Emotional Track
                    </Text>
                </ContainedButton>
            </div>

            {/* <PDFViewer>{MyDocument}</PDFViewer> */}
        </div>
    ) : (
        <NoInformation />
    );
};

PatientPresentRecord.propTypes = {
    data: PropTypes.object,
};

export default PatientPresentRecord;
